/**
 * ProductFormPage.js - 商品新建/编辑表单页
 * 支持完整的商品信息填写，包含基本信息、变体管理和图片上传
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  Divider,
  Upload,
  Image,
  message,
  Spin,
  Typography,
  InputNumber,
  Popconfirm,
  Collapse,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  UploadOutlined,
  DeleteOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { adminProductApi } from '../../api/index';
import RichTextEditor from '../../components/RichTextEditor';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

// 变体初始值模板
const emptyVariant = () => ({
  sku: '',
  title: '',
  price: '',
  compareAtPrice: '',
  inventoryQty: 0,
  option1Name: '',
  option1Value: '',
  option2Name: '',
  option2Value: '',
});

export default function ProductFormPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // 有 id 则为编辑模式
  const isEdit = !!id && id !== 'new';

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 分类列表
  const [categories, setCategories] = useState([]);
  // 变体列表（动态管理）
  const [variants, setVariants] = useState([emptyVariant()]);
  // 已上传的图片列表（编辑时从服务端加载）
  const [existingImages, setExistingImages] = useState([]);
  // 新选择的待上传文件列表
  const [uploadFileList, setUploadFileList] = useState([]);
  // 正在上传图片的 loading
  const [imageUploading, setImageUploading] = useState(false);
  // 编辑时的商品 id（用于图片上传）
  const [productId, setProductId] = useState(null);

  // 获取分类列表
  const fetchCategories = useCallback(async () => {
    try {
      const data = await adminProductApi.listCategories();
      setCategories(Array.isArray(data) ? data : data?.list || []);
    } catch {
      // 分类加载失败不阻塞
    }
  }, []);

  // 编辑模式：加载商品数据并回填表单
  const fetchProduct = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const data = await adminProductApi.getById(id);
      setProductId(data.id);
      form.setFieldsValue({
        title: data.title,
        slug: data.slug,
        status: data.status,
        description: data.description,
        bodyHtml: data.bodyHtml,
        vendor: data.vendor,
        productType: data.productType,
        tags: data.tags,
        seoTitle: data.seoTitle,
        seoDescription: data.seoDescription,
        currency: data.currency || 'USD',
        sortOrder: data.sortOrder ?? 0,
        categoryIds: data.categories?.map((c) => c.id) || [],
      });
      // 回填变体数据（后端价格单位是元）
      if (Array.isArray(data.variants) && data.variants.length > 0) {
        setVariants(
          data.variants.map((v) => ({
            id: v.id,
            sku: v.sku || '',
            title: v.title || '',
            price: v.price || '',
            compareAtPrice: v.compareAtPrice || '',
            inventoryQty: v.inventoryQty || 0,
            option1Name: v.option1Name || '',
            option1Value: v.option1Value || '',
            option2Name: v.option2Name || '',
            option2Value: v.option2Value || '',
          }))
        );
      }
      // 已有图片
      setExistingImages(data.images || []);
    } catch (err) {
      message.error('加载商品信息失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, form]);

  useEffect(() => {
    fetchCategories();
    fetchProduct();
  }, [fetchCategories, fetchProduct]);

  // 变体字段更新
  const updateVariant = (index, field, value) => {
    setVariants((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // 添加新变体
  const addVariant = () => {
    setVariants((prev) => [...prev, emptyVariant()]);
  };

  // 删除变体
  const removeVariant = (index) => {
    setVariants((prev) => prev.filter((_, i) => i !== index));
  };

  // 保存表单（新建或更新）
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // 变体价格直接使用（后端存储单位为元）
      const variantsInCents = variants.map((v, index) => ({
        ...v,
        sku: v.sku ? v.sku.trim() : `auto-${Date.now()}-${index}`,
        price: v.price ? Number(v.price) : 0,
        compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
        costPrice: v.costPrice ? Number(v.costPrice) : null,
      }));

      const payload = {
        ...values,
        variants: variantsInCents,
      };

      console.log('Saving product with variants:', variantsInCents);

      let savedProductId = productId;

      if (isEdit) {
        await adminProductApi.update(id, payload);
        message.success('商品更新成功');
      } else {
        const created = await adminProductApi.create(payload);
        savedProductId = created?.id;
        message.success('商品创建成功');
      }

      // 上传新图片（需要商品 id）
      if (uploadFileList.length > 0 && savedProductId) {
        setImageUploading(true);
        try {
          for (const file of uploadFileList) {
            const formData = new FormData();
            formData.append('file', file.originFileObj || file);
            await adminProductApi.uploadImage(savedProductId, formData);
          }
          message.success('图片上传成功');
        } catch (err) {
          message.warning('图片上传部分失败：' + (err.message || ''));
        } finally {
          setImageUploading(false);
        }
      }

      navigate('/admin/products');
    } catch (err) {
      if (err?.errorFields) {
        // 表单验证错误，antd 已自动显示
        return;
      }
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 删除已有图片
  const handleDeleteImage = async (image) => {
    try {
      await adminProductApi.deleteImage(image.id);
      setExistingImages((prev) => prev.filter((img) => img.id !== image.id));
      message.success('图片已删除');
    } catch (err) {
      message.error('删除图片失败：' + (err.message || ''));
    }
  };

  // 上传组件 beforeUpload：阻止自动上传，由保存时手动处理
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件');
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过 5MB');
      return Upload.LIST_IGNORE;
    }
    return false; // 阻止自动上传
  };

  return (
    <Spin spinning={loading || imageUploading}>
      <div>
        {/* 顶部固定操作栏 */}
        <div
          style={{
            position: 'sticky',
            top: 64, // 在 AdminLayout Header 下方
            zIndex: 99,
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/admin/products')}
            >
              返回列表
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {isEdit ? '编辑商品' : '新建商品'}
            </Title>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
            >
              {isEdit ? '保存' : '创建'}
            </Button>
            <Button onClick={() => navigate('/admin/products')}>
              取消
            </Button>
          </Space>
        </div>

        <Form form={form} layout="vertical" initialValues={{ status: 'draft', currency: 'USD' }}>
          <Row gutter={[16, 16]}>
            {/* 左侧：主要信息 */}
            <Col xs={24} lg={16}>
              {/* 基本信息卡片 */}
              <Card title="基本信息" style={{ borderRadius: 8, marginBottom: 16 }}>
                <Form.Item
                  name="title"
                  label="商品名称"
                  rules={[{ required: true, message: '请输入商品名称' }]}
                >
                  <Input placeholder="输入商品名称" />
                </Form.Item>

                <Form.Item
                  name="slug"
                  label="自定义 URL（可选）"
                  extra="用于商品详情页 URL，如 /products/your-slug。留空则自动生成"
                >
                  <Input addonBefore={`${window.location.origin}/products/`} placeholder="留空自动生成" />
                </Form.Item>

                <Form.Item name="description" label="简短描述">
                  <TextArea rows={3} placeholder="商品简短描述（用于列表展示）" />
                </Form.Item>

                <Form.Item name="bodyHtml" label="详细描述">
                  <RichTextEditor placeholder="商品详细描述，支持富文本、图片、表格等" />
                </Form.Item>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="vendor" label="供应商">
                      <Input placeholder="供应商名称" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="productType" label="商品类型">
                      <Input placeholder="商品类型标签" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="tags" label="标签（逗号分隔）">
                  <Input placeholder="例：新品,热销,推荐" />
                </Form.Item>
              </Card>

              {/* 变体管理卡片 */}
              <Card
                title="商品规格/变体"
                style={{ borderRadius: 8, marginBottom: 16 }}
                extra={
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={addVariant}
                    size="small"
                  >
                    添加变体
                  </Button>
                }
              >
                {variants.map((variant, index) => (
                  <div key={index}>
                    {index > 0 && <Divider style={{ margin: '16px 0' }} />}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                      }}
                    >
                      <Text strong style={{ fontSize: 13 }}>
                        变体 #{index + 1}
                      </Text>
                      {variants.length > 1 && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<MinusCircleOutlined />}
                          onClick={() => removeVariant(index)}
                        >
                          删除
                        </Button>
                      )}
                    </div>

                    <Row gutter={[12, 0]}>
                      <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ marginBottom: 4, fontSize: 13 }}>SKU</div>
                          <Input
                            placeholder="SKU 编码"
                            value={variant.sku}
                            onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ marginBottom: 4, fontSize: 13 }}>变体名称</div>
                          <Input
                            placeholder="如：红色 XL"
                            value={variant.title}
                            onChange={(e) => updateVariant(index, 'title', e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ marginBottom: 4, fontSize: 13 }}>库存数量</div>
                          <InputNumber
                            placeholder="库存"
                            min={0}
                            style={{ width: '100%' }}
                            value={variant.inventoryQty}
                            onChange={(v) => updateVariant(index, 'inventoryQty', v)}
                          />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ marginBottom: 4, fontSize: 13 }}>售价 ($)</div>
                          <InputNumber
                            placeholder="0.00"
                            min={0}
                            precision={2}
                            prefix="$"
                            style={{ width: '100%' }}
                            value={variant.price}
                            onChange={(v) => updateVariant(index, 'price', v)}
                          />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ marginBottom: 4, fontSize: 13 }}>划线价 ($)</div>
                          <InputNumber
                            placeholder="0.00"
                            min={0}
                            precision={2}
                            prefix="$"
                            style={{ width: '100%' }}
                            value={variant.compareAtPrice}
                            onChange={(v) => updateVariant(index, 'compareAtPrice', v)}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* 属性选项 - 折叠面板 */}
                    <Collapse
                      ghost
                      style={{ marginTop: 8 }}
                    >
                      <Panel header="属性选项（颜色/尺码等）" key="options">
                        <Row gutter={[12, 0]}>
                          <Col xs={12} sm={6}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ marginBottom: 4, fontSize: 13 }}>属性1名称</div>
                              <Input
                                placeholder="如：颜色"
                                value={variant.option1Name}
                                onChange={(e) => updateVariant(index, 'option1Name', e.target.value)}
                              />
                            </div>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ marginBottom: 4, fontSize: 13 }}>属性1值</div>
                              <Input
                                placeholder="如：红色"
                                value={variant.option1Value}
                                onChange={(e) => updateVariant(index, 'option1Value', e.target.value)}
                              />
                            </div>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ marginBottom: 4, fontSize: 13 }}>属性2名称</div>
                              <Input
                                placeholder="如：尺码"
                                value={variant.option2Name}
                                onChange={(e) => updateVariant(index, 'option2Name', e.target.value)}
                              />
                            </div>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ marginBottom: 4, fontSize: 13 }}>属性2值</div>
                              <Input
                                placeholder="如：XL"
                                value={variant.option2Value}
                                onChange={(e) => updateVariant(index, 'option2Value', e.target.value)}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  </div>
                ))}

                {variants.length === 0 && (
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={addVariant}
                  >
                    添加第一个变体
                  </Button>
                )}
              </Card>

              {/* 图片上传卡片 */}
              <Card title="商品图片" style={{ borderRadius: 8, marginBottom: 16 }}>
                {/* 已有图片预览（编辑模式） */}
                {existingImages.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                      当前图片
                    </Text>
                    <Space wrap>
                      {existingImages.map((img) => (
                        <div key={img.id} style={{ position: 'relative', display: 'inline-block' }}>
                          <Image
                            src={img.url}
                            width={80}
                            height={80}
                            style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }}
                          />
                          <Popconfirm
                            title="确认删除该图片？"
                            onConfirm={() => handleDeleteImage(img)}
                            okText="删除"
                            cancelText="取消"
                          >
                            <Button
                              type="primary"
                              danger
                              shape="circle"
                              size="small"
                              icon={<DeleteOutlined />}
                              style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                padding: 0,
                                fontSize: 10,
                              }}
                            />
                          </Popconfirm>
                        </div>
                      ))}
                    </Space>
                  </div>
                )}

                {/* 上传新图片 */}
                <Upload
                  listType="picture-card"
                  multiple
                  beforeUpload={beforeUpload}
                  fileList={uploadFileList}
                  onChange={({ fileList }) => setUploadFileList(fileList)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8, fontSize: 13 }}>上传图片</div>
                  </div>
                </Upload>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  支持 JPG / PNG / WebP，单张不超过 5MB。图片将在保存商品后上传。
                </Text>
              </Card>

              {/* SEO 信息卡片 */}
              <Card title="SEO 设置" style={{ borderRadius: 8, marginBottom: 16 }}>
                <Form.Item name="seoTitle" label="SEO 标题">
                  <Input placeholder="搜索引擎显示的标题（留空则使用商品名称）" />
                </Form.Item>
                <Form.Item name="seoDescription" label="SEO 描述">
                  <TextArea rows={3} placeholder="搜索引擎显示的描述（建议 150 字以内）" />
                </Form.Item>
              </Card>
            </Col>

            {/* 右侧：状态、分类等 */}
            <Col xs={24} lg={8}>
              {/* 发布状态卡片 */}
              <Card title="发布状态" style={{ borderRadius: 8, marginBottom: 16 }}>
                <Form.Item
                  name="status"
                  label="商品状态"
                  rules={[{ required: true, message: '请选择状态' }]}
                >
                  <Select>
                    <Option value="active">上架（公开可见）</Option>
                    <Option value="draft">草稿（不公开）</Option>
                    <Option value="archived">已归档</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="sortOrder"
                  label="展示排序"
                  extra="数字越小越靠前，默认 0；相同值按创建时间倒序。想让商品更靠前可设更小的数（如 1、0、-1）。"
                >
                  <InputNumber min={-9999} max={9999} precision={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Card>

              {/* 定价与货币卡片 */}
              <Card title="货币设置" style={{ borderRadius: 8, marginBottom: 16 }}>
                <Form.Item name="currency" label="默认货币">
                  <Select>
                    <Option value="USD">USD 美元</Option>
                    <Option value="CNY">CNY 人民币</Option>
                    <Option value="EUR">EUR 欧元</Option>
                    <Option value="GBP">GBP 英镑</Option>
                  </Select>
                </Form.Item>
              </Card>

              {/* 分类卡片 */}
              <Card title="商品分类" style={{ borderRadius: 8 }}>
                <Form.Item name="categoryIds" label="所属分类">
                  <Select
                    mode="multiple"
                    placeholder="选择一个或多个分类"
                    optionFilterProp="children"
                    showSearch
                  >
                    {categories.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </Spin>
  );
}
